import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UtilitySystemsService } from '../services/utility.service';
import { toast } from 'sonner';
import type { UtilitySystem } from '../types/utility.types';

export { useUtilitySystems as useUtility };
export { useUtilitySystemDetail as useUtilityDetail };
export { useUtilitySystemComponents as useUtilityComponents };

export function useUtilitySystems(companyId: string, projectId?: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['utility-systems', companyId, projectId],
    queryFn: () => UtilitySystemsService.getSystems(companyId, projectId),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['utility-stats', companyId, projectId],
    queryFn: () => UtilitySystemsService.getStats(companyId, projectId),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (system: Partial<UtilitySystem>) => UtilitySystemsService.createSystem(system),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['utility-systems'] });
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<UtilitySystem> }) => 
      UtilitySystemsService.updateSystem(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['utility-systems'] });
    }
  });

  return {
    systems: query.data || [],
    stats: statsQuery.data,
    isLoading: query.isLoading || statsQuery.isLoading,
    createSystem: createMutation.mutateAsync,
    updateSystem: updateMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending
  };
}

export function useUtilitySystemComponents(companyId: string, systemId?: string) {
  return useQuery({
    queryKey: ['utility-components', companyId, systemId],
    queryFn: () => UtilitySystemsService.getComponents(systemId!),
    enabled: !!systemId,
  });
}

export function useUtilitySystemDetail(id?: string) {
  const query = useQuery({
    queryKey: ['utility-systems', 'detail', id],
    queryFn: () => UtilitySystemsService.getSystemDetail(id!),
    enabled: !!id,
  });

  const componentsQuery = useQuery({
    queryKey: ['utility-components', id],
    queryFn: () => UtilitySystemsService.getComponents(id!),
    enabled: !!id,
  });

  return {
    system: query.data,
    components: componentsQuery.data || [],
    isLoading: query.isLoading || componentsQuery.isLoading,
  };
}

export function useWorkOrderUtility(workOrderId?: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['construction-work-order-utility', workOrderId],
    queryFn: () => UtilitySystemsService.getWorkOrderUtilities(workOrderId!),
    enabled: !!workOrderId,
  });

  const logInstallationMutation = useMutation({
    mutationFn: ({ linkId, qty }: { linkId: string; qty: number }) => 
      UtilitySystemsService.logInstallation(linkId, qty),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-work-order-utility', workOrderId] });
      toast.success('Ghi nhận khối lượng lắp đặt thành công');
    },
  });

  return {
    utilities: query.data || [],
    isLoading: query.isLoading,
    logInstallation: logInstallationMutation.mutateAsync,
    isLogging: logInstallationMutation.isPending,
  };
}
